/**
 * Blink whatever a colour paints, so hovering a colour field shows what it
 * edits. Runs inside the preview iframe. A flash swaps the colour for a loud
 * contrasting one a few times through a temporary stylesheet, then removes it.
 *
 * spec.vars    theme custom properties, overridden on :root and #app-container
 * spec.rules   [{ selector, property }] for colours with no shared variable
 * spec.drawer  open the side menu for the flash, since it is hidden otherwise
 */

const STYLE_ID = 'theme-color-flash';
const BLINKS = 3;
const STEP_MS = 170;
const PROPERTIES = new Set(['color', 'background-color', '--color-text']);
const VAR_NAME = /^--[a-z0-9-]+$/;
const LOUD = '#ff2d95';
const LOUD_ALT = '#00b3ff';

const THEME_COLOR_FLASH = {
    'colors.background': { vars: ['--color-background'] },
    'colors.text': { vars: ['--color-text'] },
    'colors.sideMenuBackground': { vars: ['--color-side-menu-background'], drawer: true },
    'colors.sideMenuText': { vars: ['--color-side-menu-text'], drawer: true },
    'colors.headerBackground': { vars: ['--color-header-background'] },
    'colors.headerText': { vars: ['--color-header-text'] },
    'links.color': { rules: [{ selector: '.html-page a', property: 'color' }] },
    'links.hoverColor': { rules: [{ selector: '.html-page a', property: 'color' }] },
};

/** The flash for a theme colour path, or null when it has none. */
export function themeColorFlash(path) {
    return typeof path === 'string' ? (THEME_COLOR_FLASH[path] || null) : null;
}

const VIEWER_SURFACE = '[data-viewer-root], [data-viewer-root] .bg-background';
const VIEWER_CONTROLS = '[data-viewer-root] [data-header-module^="icon-"], [data-viewer-root] [data-slideshow-counter]';
const VIEWER_TITLE = '[data-viewer-root] [data-header-module="media-title"]';

const VIEWER_COLOR_FLASH = {
    background: { rules: [{ selector: VIEWER_SURFACE, property: 'background-color' }] },
    controls: { rules: [{ selector: VIEWER_CONTROLS, property: 'color' }] },
    controlsBackground: { rules: [{ selector: VIEWER_CONTROLS, property: 'background-color' }] },
    title: { rules: [{ selector: VIEWER_TITLE, property: 'color' }] },
    titleBackground: { rules: [{ selector: VIEWER_TITLE, property: 'background-color' }] },
};

/** A viewer colour (viewer-look.js keys), flashed on what it paints in the open viewer. */
export function viewerColorFlash(key) {
    return VIEWER_COLOR_FLASH[key] || null;
}

/** Colour props on a header block, flashed on that block's own elements. */
export function headerBlockColorFlash(blockId, key) {
    if (!blockId) return null;
    const block = `[data-header-block="${String(blockId).replace(/["\\]/g, '')}"]`;
    if (key === 'drawerBackground') return { rules: [{ selector: '#menu', property: 'background-color' }], drawer: true };
    if (key === 'drawerColor' || key === 'drawerHoverColor') {
        return { rules: [{ selector: '#menu .drawer-link', property: 'color' }], drawer: true };
    }
    return {
        rules: [
            { selector: block, property: '--color-text' },
            { selector: `${block}, ${block} *`, property: 'color' },
        ],
    };
}

export function validFlashSpec(spec) {
    if (!spec || typeof spec !== 'object') return false;
    const vars = spec.vars ?? [];
    const rules = spec.rules ?? [];
    if (!Array.isArray(vars) || !Array.isArray(rules)) return false;
    if (vars.some((name) => typeof name !== 'string' || !VAR_NAME.test(name))) return false;
    if (rules.some((rule) => !rule
        || typeof rule.selector !== 'string'
        || rule.selector.length > 200
        || /[{}]/.test(rule.selector)
        || !PROPERTIES.has(rule.property))) return false;
    return vars.length + rules.length > 0;
}

function rgbOf(value) {
    const match = String(value || '').match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
    if (match) return [Number(match[1]), Number(match[2]), Number(match[3])];
    const hex = String(value || '').trim().match(/^#([0-9a-f]{6})$/i);
    if (hex) return [0, 2, 4].map((i) => parseInt(hex[1].slice(i, i + 2), 16));
    return null;
}

/** Pink, unless the colour already is pinkish; then blue. */
export function flashColorFor(value) {
    const rgb = rgbOf(value);
    if (!rgb) return LOUD;
    const [r, g, b] = rgb.map((n) => n / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max - min < 0.2) return LOUD;
    let hue;
    if (max === r) hue = ((g - b) / (max - min)) % 6;
    else if (max === g) hue = (b - r) / (max - min) + 2;
    else hue = (r - g) / (max - min) + 4;
    hue = (hue * 60 + 360) % 360;
    const distance = Math.min(Math.abs(hue - 330), 360 - Math.abs(hue - 330));
    return distance < 60 ? LOUD_ALT : LOUD;
}

function currentVar(name) {
    const host = document.getElementById('app-container') || document.documentElement;
    return getComputedStyle(host).getPropertyValue(name).trim();
}

function currentRule(rule) {
    const el = document.querySelector(rule.selector);
    if (!el) return '';
    return getComputedStyle(el).getPropertyValue(rule.property);
}

function flashCss(spec) {
    const parts = [];
    if (spec.vars?.length) {
        const decls = spec.vars
            .map((name) => `${name}: ${flashColorFor(currentVar(name))} !important;`)
            .join('');
        parts.push(`:root, #app-container {${decls}}`);
    }
    (spec.rules || []).forEach((rule) => {
        parts.push(`${rule.selector} {${rule.property}: ${flashColorFor(currentRule(rule))} !important;}`);
    });
    parts.push('#app-container *, #app-container *::before, #app-container *::after { transition: none !important; }');
    return parts.join('\n');
}

/**
 * @param {{ openDrawer?: () => void, closeDrawer?: () => void, drawerOpen?: () => boolean }} hooks
 */
export function createColorFlash(hooks = {}) {
    let timers = [];
    let openedDrawer = false;

    const styleEl = () => {
        let el = document.getElementById(STYLE_ID);
        if (!el) {
            el = document.createElement('style');
            el.id = STYLE_ID;
            document.head.appendChild(el);
        }
        return el;
    };

    const stop = () => {
        timers.forEach(clearTimeout);
        timers = [];
        document.getElementById(STYLE_ID)?.remove();
        if (openedDrawer) {
            openedDrawer = false;
            hooks.closeDrawer?.();
        }
    };

    const start = (spec) => {
        stop();
        if (!validFlashSpec(spec)) return;
        let delay = 0;
        if (spec.drawer && !hooks.drawerOpen?.()) {
            hooks.openDrawer?.();
            openedDrawer = true;
            delay = 260;
        }
        const run = () => {
            const css = flashCss(spec);
            const el = styleEl();
            for (let i = 0; i < BLINKS * 2; i += 1) {
                timers.push(setTimeout(() => {
                    el.textContent = i % 2 === 0 ? css : '';
                }, i * STEP_MS));
            }
            timers.push(setTimeout(() => {
                el.textContent = '';
            }, BLINKS * 2 * STEP_MS));
        };
        if (delay) timers.push(setTimeout(run, delay));
        else run();
    };

    return { start, stop };
}
